import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import {
  X, Clock, MessageSquare, Plus, Trash2, Send, Link2,
  Layers, Package, BookOpen, CheckSquare, ChevronDown, ChevronUp, Activity, ArrowUpRight, Bug, Github, Square, Check, ShieldAlert, Lightbulb, Contact, Handshake, PlugZap, MessagesSquare
} from 'lucide-react';
import toast from 'react-hot-toast';
import { itemRef } from '../../utils/itemRef';
import ApprovalPanel from '../approvals/ApprovalPanel';
import RunPanel from '../training/RunPanel';
import { useAppStore } from '../../store/useAppStore';
import { useWorkItemStore } from '../../store/useWorkItemStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useResourceStore } from '../../store/useResourceStore';
import { useAuthStore } from '../../store/useAuthStore';
import { useProjectStore } from '../../store/useProjectStore';
import { useUserStore } from '../../store/useUserStore';
import { usePIStore } from '../../store/usePIStore';
import { commentApi, timeEntryApi, activityApi, workItemApi, workflowApi } from '../../services/api';
import type { WorkItem, Comment, TimeEntry, Activity as ActivityType, WorkItemType } from '../../types';
import CreateWorkItemModal from './CreateWorkItemModal';
import BugReportModal from './BugReportModal';
import OpportunityOrgs from '../crm/OpportunityOrgs';
import OpportunityDeal from '../crm/OpportunityDeal';
import OpportunityPaperwork from '../crm/OpportunityPaperwork';
import DependencyEditor from './DependencyEditor';
import { CheckoutControls } from './CheckoutBadge';
import WaysConversation from '../ways/WaysConversation';

const typeIcons: Record<string, typeof Layers> = {
  epic: Layers, feature: Package, story: BookOpen, task: CheckSquare, bug: Bug, security: ShieldAlert, idea: Lightbulb, lead: Contact, opportunity: Handshake, integration: PlugZap,
};

const ALLOWED_PARENT_TYPES: Record<WorkItemType, WorkItemType[]> = {
  epic: [],
  feature: ['epic'],
  story: ['feature'],
  task: ['epic', 'feature', 'story', 'task'],
  bug: ['epic', 'feature', 'story'],
  security: ['epic', 'feature', 'story'],
  design: ['epic', 'feature', 'story'],
  idea: [],
  lead: [],
  opportunity: [],
  // Auto-created by the dispatcher on dead-letter, so it has no natural
  // parent and must never be given one by hand.
  integration: [],
};

const SEVERITY_BADGE: Record<string, string> = {
  info: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-200',
  low: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-200',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
};

const SECURITY_SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
const SECURITY_REMEDIATION = ['triage', 'confirmed', 'in_remediation', 'mitigated', 'fixed', 'wont_fix', 'duplicate'] as const;
const SECURITY_DISCLOSURE = ['internal', 'coordinated', 'public', 'embargoed'] as const;
const SECURITY_DISCOVERY = ['manual_review', 'sast', 'dast', 'sca', 'pentest', 'bug_bounty', 'external_disclosure', 'incident'] as const;

export default function WorkItemDetailSlideout() {
  const { detailItemId, setDetailItemId } = useAppStore();
  const { items, updateItem, deleteItem, fetchByProject } = useWorkItemStore();
  const { config, getWorkItemTypeConfig, getStatusColor } = useConfigStore();
  const { canDeleteWorkItem, hasModule } = useAuthStore();
  const { projects } = useProjectStore();
  const resources = useResourceStore((s) => s.resources);
  const activeResources = useMemo(() => resources.filter((r) => r.active), [resources]);
  const pmoUsers = useUserStore((s) => s.users);
  const piSprints = usePIStore((s) => s.sprints);
  const piList = usePIStore((s) => s.pis);

  // Resolve an email to a PMO user name (checks primary + additionalEmails)
  const resolveEmailToName = (email: string): string | null => {
    const user = pmoUsers.find(
      (u) => u.email === email || (u.additionalEmails || []).includes(email)
    );
    return user ? user.name : null;
  };

  const [item, setItem] = useState<WorkItem | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [newComment, setNewComment] = useState('');
  const [timeForm, setTimeForm] = useState({ hours: '', description: '' });
  const [showAddChild, setShowAddChild] = useState(false);
  const [bugReportCriteria, setBugReportCriteria] = useState<{ text: string; index: number } | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['details', 'security', 'children', 'comments'])
  );

  const isOpen = detailItemId !== null;
  const navigate = useNavigate();
  // Leads and opportunities are CRM records, not delivery work: no sprint,
  // points, due date, sub-items or time log, and their stages come from their
  // own pipeline rather than the global status list.
  const isCrm = item?.workItemType === 'lead' || item?.workItemType === 'opportunity';
  const [crmStages, setCrmStages] = useState<{ id: string; label: string }[]>([]);

  // ---- Deep-linking: keep detailItemId in sync with the ?item=<_id> query param ----
  const [searchParams, setSearchParams] = useSearchParams();

  // URL -> store: a pasted/refreshed link (?item=…) opens the drawer.
  useEffect(() => {
    const urlItem = searchParams.get('item');
    if (urlItem && urlItem !== detailItemId) {
      setDetailItemId(urlItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // store -> URL: opening/closing an item updates the address bar so it can be copied.
  useEffect(() => {
    const urlItem = searchParams.get('item');
    if (detailItemId && detailItemId !== urlItem) {
      const next = new URLSearchParams(searchParams);
      next.set('item', detailItemId);
      setSearchParams(next, { replace: true });
    } else if (!detailItemId && urlItem) {
      const next = new URLSearchParams(searchParams);
      next.delete('item');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailItemId]);

  // Copy a shareable link to this work item. We use the _id permalink
  // (/item/<_id>) — the only globally-unique, stable identifier. displayId-based
  // paths (PO-1/EP-1/…) are NOT unique (projects and items are numbered per
  // parent), so they can resolve to the wrong item and must not be shared.
  // Resolved against the current origin so it works on whatever host you're on
  // (pmo.hls.local internally, pmo.hsinc.ai publicly).
  const handleCopyLink = () => {
    const rel = (item as any)?.permalink || (detailItemId ? `/item/${detailItemId}` : null);
    if (!rel) return;
    const url = `${window.location.origin}${rel}`;
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Could not copy link'));
  };

  useEffect(() => {
    if (detailItemId) {
      // Show the store's copy immediately (may be the board's light projection),
      // then fetch the full document so heavy fields (description, acceptance
      // criteria, done-when, customFields) are present regardless of projection.
      const found = items.find((i) => i._id === detailItemId);
      setItem(found || null);
      workItemApi
        .get(detailItemId)
        .then((full) => {
          if (full) setItem(full);
        })
        .catch(() => {});

      // Load related data
      commentApi.list(detailItemId).then(setComments).catch(() => {});
      timeEntryApi.list(detailItemId).then(setTimeEntries).catch(() => {});
      activityApi.list({ targetId: detailItemId, limit: 20 }).then(setActivities).catch(() => {});
    }
  }, [detailItemId, items]);

  useEffect(() => {
    if (!isCrm || !item?._id) { setCrmStages([]); return; }
    workflowApi.transitionsFor(item._id)
      .then((t) => setCrmStages((t.states || []).map((st: any) => ({ id: st.id, label: st.label || st.id }))))
      .catch(() => setCrmStages([]));
  }, [isCrm, item?._id, item?.status]);

  const refetchItem = () => {
    if (item) workItemApi.get(item._id).then((full) => full && setItem(full)).catch(() => {});
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(section) ? next.delete(section) : next.add(section);
      return next;
    });
  };

  const handleFieldUpdate = async (field: string, value: any) => {
    if (!item) return;
    try {
      await updateItem(item._id, { [field]: value });
    } catch (e: any) {
      if (e?.awaitingApproval) {
        // Filed for approval (EP-26 FE-7): show it on the item; the status is unchanged.
        setItem({ ...item, pendingApproval: e.awaitingApproval });
        toast(e.message, { icon: '⏳' });
        return;
      }
      toast.error(e?.message || 'Failed to update');
    }
  };

  const handleAddComment = async () => {
    if (!item || !newComment.trim()) return;
    try {
      const comment = await commentApi.create(item._id, { body: newComment });
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } catch {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await commentApi.delete(commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  const handleLogTime = async () => {
    if (!item || !timeForm.hours) return;
    try {
      const entry = await timeEntryApi.create(item._id, {
        hours: parseFloat(timeForm.hours),
        description: timeForm.description,
      });
      setTimeEntries((prev) => [entry, ...prev]);
      setTimeForm({ hours: '', description: '' });
      // Refresh work items to get updated loggedHours
      if (item.projectId) fetchByProject(item.projectId);
    } catch {
      toast.error('Failed to log time');
    }
  };

  const handleDelete = async () => {
    if (!item || !confirm('Delete this work item and all children?')) return;
    try {
      await deleteItem(item._id);
      setDetailItemId(null);
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const children = item ? items.filter((i) => i.parentId === item._id) : [];
  const typeConfig = item ? getWorkItemTypeConfig(item.workItemType) : null;
  const Icon = item ? typeIcons[item.workItemType] || CheckSquare : CheckSquare;

  // Build parent breadcrumb chain
  const parentChain = useMemo(() => {
    if (!item) return [];
    const chain: WorkItem[] = [];
    let cur = item.parentId ? items.find((i) => i._id === item.parentId) : undefined;
    while (cur) {
      chain.unshift(cur);
      cur = cur.parentId ? items.find((i) => i._id === cur!.parentId) : undefined;
    }
    return chain;
  }, [item, items]);

  // Potential parents for re-parenting
  const potentialParents = useMemo(() => {
    if (!item) return [];
    const allowed = ALLOWED_PARENT_TYPES[item.workItemType as WorkItemType] || [];
    if (allowed.length === 0) return [];
    return items.filter(
      (i) =>
        i._id !== item._id &&
        allowed.includes(i.workItemType as WorkItemType)
    );
  }, [item, items]);

  return (
    <>
      <Transition show={isOpen}>
        <div className="fixed inset-y-0 right-0 z-40 flex">
          {/* Overlay */}
          <Transition.Child
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black/20"
              onClick={() => setDetailItemId(null)}
            />
          </Transition.Child>

          {/* Panel */}
          <Transition.Child
            enter="ease-out duration-200"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="relative w-[560px] h-screen bg-white dark:bg-gray-800 shadow-xl border-l border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
              {item && typeConfig && (
                <>
                  {/* Header */}
                  <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon size={16} style={{ color: typeConfig.color }} />
                        <span
                          className="px-1.5 py-0.5 text-xs font-medium rounded"
                          style={{ backgroundColor: typeConfig.color + '15', color: typeConfig.color }}
                        >
                          {typeConfig.label}
                        </span>
                        {/* The estate-unique key, not the per-parent badge —
                            this is the reference a person copies into a comment
                            or a commit message. Click to copy it. */}
                        {itemRef(item) && (
                          <button
                            type="button"
                            title="Copy reference"
                            onClick={() => {
                              navigator.clipboard
                                .writeText(itemRef(item))
                                .then(() => toast.success(`${itemRef(item)} copied`))
                                .catch(() => toast.error('Could not copy'));
                            }}
                            className="text-xs font-mono text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            {itemRef(item)}
                          </button>
                        )}
                        {item.workItemType === 'security' && Boolean(item.customFields?.severity) && (
                          <span
                            className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
                              SEVERITY_BADGE[String(item.customFields?.severity ?? '')] || SEVERITY_BADGE.medium
                            }`}
                            title="Security severity"
                          >
                            {String(item.customFields?.severity ?? '')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={handleCopyLink}
                          title="Copy link to this item"
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          <Link2 size={14} />
                        </button>
                        {canDeleteWorkItem() && (
                          <button
                            onClick={handleDelete}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setDetailItemId(null)}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Editable title */}
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => setItem({ ...item, title: e.target.value })}
                      onBlur={(e) => handleFieldUpdate('title', e.target.value)}
                      className="w-full text-lg font-semibold text-gray-900 dark:text-gray-100 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                    />

                    {/* Status row */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <CheckoutControls item={item} onChanged={() => workItemApi.get(item._id).then((full) => full && setItem(full)).catch(() => {})} />
                      {item.pendingApproval && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                          title={`Requested by ${item.pendingApproval.requestedBy}. Decided by holders of ${item.pendingApproval.permission}; the requester cannot approve it.`}
                        >
                          Awaiting approval {item.pendingApproval.approvals}/{item.pendingApproval.required}
                          {' → '}{config.statuses.find((s) => s.id === item.pendingApproval?.to)?.label || item.pendingApproval.to}
                          <span className="text-amber-600 dark:text-amber-400">· needs {item.pendingApproval.permission}</span>
                        </span>
                      )}
                      <select
                        value={item.status}
                        onChange={(e) => handleFieldUpdate('status', e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
                      >
                        {(isCrm && crmStages.length ? crmStages : config.statuses).map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>

                      <select
                        value={item.priority}
                        onChange={(e) => handleFieldUpdate('priority', e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
                      >
                        {config.priorities.map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>

                      <select
                        value={item.assignee}
                        onChange={(e) => handleFieldUpdate('assignee', e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent w-32"
                      >
                        <option value="">Unassigned</option>
                        {activeResources.filter((r) => r.resourceType === 'user').length > 0 && (
                          <optgroup label="Users">
                            {activeResources.filter((r) => r.resourceType === 'user').map((r) => (
                              <option key={r._id} value={r.name}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {activeResources.filter((r) => r.resourceType === 'token').length > 0 && (
                          <optgroup label="Tokens">
                            {activeResources.filter((r) => r.resourceType === 'token').map((r) => (
                              <option key={r._id} value={r.name}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                        {activeResources.filter((r) => r.resourceType === 'resource' || !r.resourceType).length > 0 && (
                          <optgroup label="Resources">
                            {activeResources.filter((r) => r.resourceType === 'resource' || !r.resourceType).map((r) => (
                              <option key={r._id} value={r.name}>{r.name}</option>
                            ))}
                          </optgroup>
                        )}
                      </select>

                      {!isCrm && (
                        <>
                        <input
                          type="date"
                          value={item.startDate || ''}
                          onChange={(e) => handleFieldUpdate('startDate', e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
                          title="Start Date"
                        />

                        <input
                          type="date"
                          value={item.dueDate}
                          onChange={(e) => handleFieldUpdate('dueDate', e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
                          title="Due Date"
                        />

                        {/* Sprint assignment */}
                        <select
                          value={item.sprintId || ''}
                          onChange={(e) => handleFieldUpdate('sprintId', e.target.value || '')}
                          className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent"
                          title="Sprint"
                        >
                          <option value="">No Sprint</option>
                          {piList.map((pi) => {
                            const sprints = piSprints.filter((s) => s.piId === pi._id).sort((a, b) => a.sprintNumber - b.sprintNumber);
                            if (sprints.length === 0) return null;
                            return (
                              <optgroup key={pi._id} label={pi.name}>
                                {sprints.map((s) => (
                                  <option key={s._id} value={s._id}>{s.name}</option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </select>

                        </>
                      )}

                      {item.githubUser && (() => {
                        const resolvedName = resolveEmailToName(item.githubUser);
                        return (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md" title={`Git user: ${item.githubUser}`}>
                            <Github className="w-3 h-3" />
                            {resolvedName || item.githubUser}
                          </span>
                        );
                      })()}

                      {item.createdByTokenId && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 rounded-md" title={`Created by token: ${item.createdByTokenId}`}>
                          Token
                        </span>
                      )}
                    </div>

                    {/* A move of this item awaiting approval (EP-26 FE-7): what is asked,
                        the votes so far with their channel, the expiry, and — for someone
                        the server says may decide it — approve or deny. */}
                    {item.pendingApproval?.requestId && (
                      <div className="mt-3">
                        <ApprovalPanel
                          key={item.pendingApproval.requestId}
                          requestId={item.pendingApproval.requestId}
                          compact
                          onChanged={() => {
                            workItemApi.get(item._id).then((full) => full && setItem(full)).catch(() => {});
                          }}
                        />
                      </div>
                    )}

                    {/* A training run task shows its run record live (EP-26 FE-5 ST-4). */}
                    {(() => {
                      const tr = (item.customFields as Record<string, any> | undefined)?.training;
                      return tr?.kind === 'run' && tr?.programId ? (
                        <div className="mt-3">
                          <RunPanel key={item._id} programId={String(tr.programId)} taskId={item._id} />
                        </div>
                      ) : null;
                    })()}
                  </div>

                  {/* Parent / Hierarchy section */}
                  <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-2 space-y-2">
                    {/* Breadcrumb chain */}
                    {parentChain.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                        <ArrowUpRight size={12} className="text-gray-400 shrink-0" />
                        {parentChain.map((p, idx) => {
                          const PIcon = typeIcons[p.workItemType] || CheckSquare;
                          const pConf = getWorkItemTypeConfig(p.workItemType);
                          return (
                            <span key={p._id} className="flex items-center gap-1">
                              {idx > 0 && <span className="text-gray-300 dark:text-gray-600">›</span>}
                              <button
                                onClick={() => setDetailItemId(p._id)}
                                className="flex items-center gap-1 hover:text-accent transition-colors"
                              >
                                <PIcon size={11} style={{ color: pConf.color }} />
                                <span>{p.title}</span>
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Project dropdown for epics (mandatory) */}
                    {item.workItemType === 'epic' && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Project <span className="text-red-500">*</span>:</span>
                        <select
                          value={item.projectId || ''}
                          onChange={(e) => handleFieldUpdate('projectId', e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent flex-1"
                        >
                          <option value="">— Select Project —</option>
                          {projects.map((p) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Parent dropdown for non-epic types */}
                    {item.workItemType !== 'epic' && potentialParents.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Parent:</span>
                        <select
                          value={item.parentId || ''}
                          onChange={(e) => handleFieldUpdate('parentId', e.target.value || null)}
                          className="px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-1 focus:ring-accent flex-1"
                        >
                          <option value="">— None —</option>
                          {potentialParents.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.title} ({p.workItemType})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Scrollable body */}
                  <div className="flex-1 overflow-y-auto">
                    {/* Who buys and who sells. Above the description for the
                        same reason as the lead block: on an opportunity these
                        ARE the item, and without them a won deal silently
                        fails to originate anything. */}
                    {item.workItemType === 'opportunity' && (
                      <>
                        <OpportunityDeal item={item} onSaved={refetchItem} />
                        <OpportunityOrgs item={item} onChange={handleFieldUpdate} />
                        <OpportunityPaperwork opportunityId={item._id} />
                      </>
                    )}

                    {/* What blocks this. WSJF ranks siblings — tasks within a
                        story, stories within a feature — so the ordering inside
                        a parent IS the plan. A task waiting on a task under a
                        different story is the case ordering cannot express, and
                        this is where it gets recorded. Read-only until now:
                        every dependency in the estate had to go through the
                        API, though the Kanban badge and Gantt arrows have been
                        drawing them all along. */}
                    {!['idea', 'lead', 'opportunity'].includes(item.workItemType) && (
                      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                        <DependencyEditor item={item} onChange={handleFieldUpdate} />
                      </div>
                    )}

                    {/* Lead contact + attribution (CRM FE-2 ST-2).
                        Placed above the description because for a lead the
                        contact details ARE the item — the description is just
                        the message they sent. */}
                    {item.workItemType === 'lead' && (
                      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 space-y-3">
                        {/* Converting creates the opportunity; moving the stage
                            alone does not, and the server refuses it. */}
                        {item.convertedToId ? (
                          <button
                            onClick={() => setDetailItemId(item.convertedToId!)}
                            className="flex items-center gap-1.5 text-xs text-accent hover:underline"
                          >
                            <Handshake size={12} /> Open the opportunity it became
                          </button>
                        ) : item.status !== 'disqualified' && (
                          <button
                            onClick={() => { setDetailItemId(null); navigate(`/crm/leads?convert=${encodeURIComponent(item._id)}`); }}
                            className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-accent text-white"
                          >
                            <Handshake size={12} /> Convert to opportunity
                          </button>
                        )}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          {[
                            ['Name', item.leadName],
                            ['Email', item.leadEmail],
                            ['Organisation', item.leadOrganisation],
                            ['Phone', item.leadPhone],
                          ].filter(([, v]) => v).map(([k, v]) => (
                            <div key={String(k)}>
                              <div className="text-[10px] uppercase tracking-wider text-gray-400">{k}</div>
                              <div className="text-gray-900 dark:text-gray-100 break-words">
                                {k === 'Email'
                                  ? <a href={`mailto:${v}`} className="hover:text-accent">{v}</a>
                                  : v}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* How they arrived. The first question anyone asks of
                            a pipeline, and unanswerable without this. */}
                        {(item.leadSource || item.leadPage || item.leadReferrer
                          || Object.keys(item.leadUtm || {}).length > 0) && (
                          <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2 space-y-1">
                            <div className="text-[10px] uppercase tracking-wider text-gray-400">
                              How they arrived
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                              {item.leadSource && <span>source: <strong>{item.leadSource}</strong></span>}
                              {item.leadPage && <span>page: {item.leadPage}</span>}
                              {item.leadReferrer && (
                                <span className="truncate max-w-full">referrer: {item.leadReferrer}</span>
                              )}
                            </div>
                            {Object.keys(item.leadUtm || {}).length > 0 && (
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {Object.entries(item.leadUtm || {}).map(([k, v]) => (
                                  <span key={k}
                                    className="px-1.5 py-0.5 rounded text-[10px] bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                    {k}: {String(v)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Repeat enquiries, attached rather than filed as
                            separate leads (CRM FE-3). */}
                        {(item.leadSubmissions || []).length > 0 && (
                          <div className="space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-gray-400">
                              {(item.leadSubmissions || []).length} repeat enquir
                              {(item.leadSubmissions || []).length === 1 ? 'y' : 'ies'} from this address
                            </div>
                            {(item.leadSubmissions || []).map((sub: any, i: number) => (
                              <div key={i}
                                className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                                <div className="text-[10px] text-amber-700 dark:text-amber-400">
                                  {sub.receivedAt ? new Date(sub.receivedAt).toLocaleString() : ''}
                                  {sub.source ? ` · ${sub.source}` : ''}
                                  {sub.page ? ` · ${sub.page}` : ''}
                                </div>
                                <div className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap mt-0.5">
                                  {sub.message}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {item.leadDisqualifiedReason && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400">
                              Disqualified:
                            </span>{' '}
                            {item.leadDisqualifiedReason}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <SectionHeader
                      title="Description"
                      section="details"
                      expanded={expandedSections.has('details')}
                      onToggle={toggleSection}
                    />
                    {expandedSections.has('details') && (
                      <div className="px-4 pb-4">
                        <textarea
                          value={item.description}
                          onChange={(e) => setItem({ ...item, description: e.target.value })}
                          onBlur={(e) => handleFieldUpdate('description', e.target.value)}
                          placeholder="Add a description..."
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent resize-none"
                          rows={4}
                        />

                        {!isCrm && <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Story Points</label>
                            {/* Any value 0-100 to two decimals; saved on blur so typing
                                "0.8" does not save "0" first. The list only offers quick picks. */}
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.05"
                              list="story-point-picks-detail"
                              value={item.storyPoints ?? ''}
                              onChange={(e) => setItem({ ...item, storyPoints: parseFloat(e.target.value) || 0 })}
                              onBlur={(e) => {
                                const sp = Math.round((parseFloat(e.target.value) || 0) * 100) / 100;
                                setItem({ ...item, storyPoints: sp });
                                handleFieldUpdate('storyPoints', sp);
                              }}
                              className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                            />
                            <datalist id="story-point-picks-detail">
                              {[0.25, 0.5, 1, 2, 3, 5, 8, 13].map((v) => (
                                <option key={v} value={v} />
                              ))}
                            </datalist>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Estimated Hours</label>
                            <input
                              type="number"
                              value={item.estimatedHours}
                              onChange={(e) => setItem({ ...item, estimatedHours: parseFloat(e.target.value) || 0 })}
                              onBlur={(e) => handleFieldUpdate('estimatedHours', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                              min="0"
                              step="0.5"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Logged Hours</label>
                            <p className="px-2 py-1 text-sm text-gray-800 dark:text-gray-200">{item.loggedHours}h</p>
                          </div>
                          {item.storyPoints > 0 && item.loggedHours > 0 && (
                            <div>
                              <label className="text-xs text-gray-500 dark:text-gray-400">Variance</label>
                              <p className={`px-2 py-1 text-sm ${(item.estimatedHours - item.loggedHours) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(item.estimatedHours - item.loggedHours).toFixed(1)}h
                              </p>
                            </div>
                          )}
                        </div>}

                        {/* WSJF Scoring Panel — only for Epic, Feature, Story */}
                        {['epic', 'feature', 'story'].includes(item.workItemType) && (
                          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide">WSJF Scoring</h4>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-[10px] text-gray-500 dark:text-gray-400">Business Value</label>
                                <select
                                  value={item.businessValue || ''}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value) || 0;
                                    setItem({ ...item, businessValue: v });
                                    handleFieldUpdate('businessValue', v);
                                  }}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                                >
                                  <option value="">—</option>
                                  {[1, 2, 3, 5, 8, 13, 21].map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] text-gray-500 dark:text-gray-400">Time Criticality</label>
                                <select
                                  value={item.timeCriticality || ''}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value) || 0;
                                    setItem({ ...item, timeCriticality: v });
                                    handleFieldUpdate('timeCriticality', v);
                                  }}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                                >
                                  <option value="">—</option>
                                  {[1, 2, 3, 5, 8, 13, 21].map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] text-gray-500 dark:text-gray-400">Risk Reduction</label>
                                <select
                                  value={item.riskReductionOpportunity || ''}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value) || 0;
                                    setItem({ ...item, riskReductionOpportunity: v });
                                    handleFieldUpdate('riskReductionOpportunity', v);
                                  }}
                                  className="w-full px-1.5 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent"
                                >
                                  <option value="">—</option>
                                  {[1, 2, 3, 5, 8, 13, 21].map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <div>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">Job Size</span>
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{item.jobSize || '—'}</p>
                              </div>
                              <div>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">WSJF Score</span>
                                <p className="text-sm font-bold text-accent">{item.wsjfScore ? item.wsjfScore.toFixed(2) : '—'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Security customFields editor (only for security items) */}
                    {item.workItemType === 'security' && (() => {
                      const cf: Record<string, any> = item.customFields || {};
                      const setCF = (field: string, value: any) => {
                        const next = { ...cf, [field]: value };
                        setItem({ ...item, customFields: next });
                        handleFieldUpdate('customFields', next);
                      };
                      const sectionKey = 'security';
                      return (
                        <>
                          <SectionHeader
                            title="Security Triage"
                            section={sectionKey}
                            expanded={expandedSections.has(sectionKey)}
                            onToggle={toggleSection}
                          />
                          {expandedSections.has(sectionKey) && (
                            <div className="px-4 pb-4 space-y-3">
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Severity</label>
                                  <select value={cf.severity || ''} onChange={(e) => setCF('severity', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded">
                                    {SECURITY_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Remediation</label>
                                  <select value={cf.remediationStatus || ''} onChange={(e) => setCF('remediationStatus', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded">
                                    {SECURITY_REMEDIATION.map((s) => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Disclosure</label>
                                  <select value={cf.disclosureStage || ''} onChange={(e) => setCF('disclosureStage', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded">
                                    {SECURITY_DISCLOSURE.map((s) => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">CVSS Score</label>
                                  <input
                                    type="number"
                                    min={0}
                                    max={10}
                                    step={0.1}
                                    value={cf.cvssScore ?? ''}
                                    onChange={(e) => setCF('cvssScore', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                    className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Discovery</label>
                                  <select value={cf.discoveryMethod || ''} onChange={(e) => setCF('discoveryMethod', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded">
                                    <option value="">—</option>
                                    {SECURITY_DISCOVERY.map((s) => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                </div>
                              </div>
                              <div>
                                <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">OWASP Category</label>
                                <input
                                  type="text"
                                  value={cf.owaspCategory || ''}
                                  onChange={(e) => setCF('owaspCategory', e.target.value)}
                                  placeholder="e.g. A02:2021 - Cryptographic Failures"
                                  className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">CVE IDs (comma-separated)</label>
                                  <input
                                    type="text"
                                    value={Array.isArray(cf.cveIds) ? cf.cveIds.join(', ') : (cf.cveIds || '')}
                                    onChange={(e) => setCF('cveIds', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                    className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">CWE IDs (comma-separated)</label>
                                  <input
                                    type="text"
                                    value={Array.isArray(cf.cweIds) ? cf.cweIds.join(', ') : (cf.cweIds || '')}
                                    onChange={(e) => setCF('cweIds', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                    className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Affected Components (comma-separated)</label>
                                <input
                                  type="text"
                                  value={Array.isArray(cf.affectedComponents) ? cf.affectedComponents.join(', ') : (cf.affectedComponents || '')}
                                  onChange={(e) => setCF('affectedComponents', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                                  className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                />
                              </div>
                              <div>
                                <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Mitigation Notes</label>
                                <textarea
                                  rows={3}
                                  value={cf.mitigationNotes || ''}
                                  onChange={(e) => setCF('mitigationNotes', e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded resize-none font-mono"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Disclosure Deadline</label>
                                  <input
                                    type="date"
                                    value={cf.disclosureDeadline || ''}
                                    onChange={(e) => setCF('disclosureDeadline', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Verified By</label>
                                  <input
                                    type="text"
                                    value={cf.verifiedBy || ''}
                                    onChange={(e) => setCF('verifiedBy', e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* Acceptance Criteria (stories) / Done When (tasks) */}
                    {(item.workItemType === 'story' || item.workItemType === 'task') && (() => {
                      const fieldName = item.workItemType === 'story' ? 'acceptanceCriteria' : 'doneWhen';
                      const sectionTitle = item.workItemType === 'story' ? 'Acceptance Criteria' : 'Done When';
                      const sectionKey = fieldName;
                      const checklist: Array<{ text: string; checked: boolean }> = (item as any)[fieldName] || [];

                      const updateChecklist = (newList: Array<{ text: string; checked: boolean }>) => {
                        setItem({ ...item, [fieldName]: newList } as any);
                        handleFieldUpdate(fieldName, newList);
                      };

                      return (
                        <>
                          <SectionHeader
                            title={`${sectionTitle} (${checklist.filter(c => c.checked).length}/${checklist.length})`}
                            section={sectionKey}
                            expanded={expandedSections.has(sectionKey)}
                            onToggle={toggleSection}
                          />
                          {expandedSections.has(sectionKey) && (
                            <div className="px-4 pb-4 space-y-1">
                              {checklist.map((ci, idx) => (
                                <div key={idx} className="flex items-start gap-2 group">
                                  <button
                                    onClick={() => {
                                      const updated = [...checklist];
                                      updated[idx] = { ...ci, checked: !ci.checked };
                                      updateChecklist(updated);
                                    }}
                                    className="mt-0.5 shrink-0"
                                  >
                                    {ci.checked ? (
                                      <Check className="w-4 h-4 text-green-500" />
                                    ) : (
                                      <Square className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    )}
                                  </button>
                                  <input
                                    type="text"
                                    value={ci.text}
                                    onChange={(e) => {
                                      const updated = [...checklist];
                                      updated[idx] = { ...ci, text: e.target.value };
                                      setItem({ ...item, [fieldName]: updated } as any);
                                    }}
                                    onBlur={() => updateChecklist(checklist)}
                                    className={`flex-1 text-sm bg-transparent border-b border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-accent focus:outline-none py-0.5 ${ci.checked ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-800 dark:text-gray-200'}`}
                                  />
                                  <button
                                    onClick={() => setBugReportCriteria({ text: ci.text, index: idx })}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                                    title="Create bug report from this criteria"
                                  >
                                    <Bug className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const updated = checklist.filter((_, i) => i !== idx);
                                      updateChecklist(updated);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  updateChecklist([...checklist, { text: '', checked: false }]);
                                }}
                                className="flex items-center gap-1 text-xs text-gray-500 hover:text-accent mt-1"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                Add item
                              </button>
                            </div>
                          )}

                          {/* Bug Report Modal */}
                          {bugReportCriteria && (
                            <BugReportModal
                              sourceItem={{
                                _id: item._id,
                                title: item.title,
                                workItemType: item.workItemType,
                                parentId: item.parentId,
                                projectId: item.projectId,
                              }}
                              criteriaText={bugReportCriteria.text}
                              criteriaType={fieldName as 'acceptanceCriteria' | 'doneWhen'}
                              onClose={() => setBugReportCriteria(null)}
                            />
                          )}
                        </>
                      );
                    })()}

                    {!isCrm && (
                      <>
                      {/* Children */}
                      <SectionHeader
                        title={`Sub-items (${children.length})`}
                        section="children"
                        expanded={expandedSections.has('children')}
                        onToggle={toggleSection}
                      />
                      {expandedSections.has('children') && (
                        <div className="px-4 pb-4">
                          {children.map((child) => {
                            const childConfig = getWorkItemTypeConfig(child.workItemType);
                            return (
                              <button
                                key={child._id}
                                onClick={() => setDetailItemId(child._id)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 rounded text-left"
                              >
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: getStatusColor(child.status) }}
                                />
                                <span
                                  className="text-[10px] font-medium px-1 rounded"
                                  style={{ backgroundColor: childConfig.color + '15', color: childConfig.color }}
                                >
                                  {childConfig.label}
                                </span>
                                <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                                  {itemRef(child) && <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 mr-1">{itemRef(child)}</span>}
                                  {child.title}
                                </span>
                              </button>
                            );
                          })}
                          <button
                            onClick={() => setShowAddChild(true)}
                            className="flex items-center gap-1.5 px-2 py-1.5 text-xs text-accent hover:bg-accent/5 rounded mt-1"
                          >
                            <Plus size={12} />
                            Add sub-item
                          </button>
                        </div>
                      )}

                                          </>
                    )}

                    {/* Comments */}
                    <SectionHeader
                      title={`Comments (${comments.length})`}
                      section="comments"
                      expanded={expandedSections.has('comments')}
                      onToggle={toggleSection}
                      icon={<MessageSquare size={14} />}
                    />
                    {expandedSections.has('comments') && (
                      <div className="px-4 pb-4">
                        <div className="space-y-2 mb-3">
                          {comments.map((c) => (
                            <div key={c._id} className="bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 group">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  {c.author || 'Anonymous'}
                                  {/* Written by a token for its owner (a runner, an agent, a training
                                      token): the actor shown beside the person it acts for. */}
                                  {c.viaToken?.name && (
                                    <span className="ml-1 font-normal text-gray-400" title={c.viaToken.id}>
                                      via {c.viaToken.name}
                                    </span>
                                  )}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-gray-400">
                                    {new Date(c.createdAt).toLocaleDateString()}
                                  </span>
                                  <button
                                    onClick={() => handleDeleteComment(c._id)}
                                    className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500"
                                  >
                                    <Trash2 size={10} />
                                  </button>
                                </div>
                              </div>
                              <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{c.body}</p>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
                            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                          />
                          <button
                            onClick={handleAddComment}
                            disabled={!newComment.trim()}
                            className="p-1.5 bg-accent text-white rounded-lg disabled:opacity-30"
                          >
                            <Send size={14} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Conversation in Ways (PO-92 F5.1 S5.1.2): collapsed, so nothing is fetched until opened */}
                    {hasModule('ways') && (
                      <>
                        <SectionHeader
                          title="Conversation (Ways)"
                          section="ways"
                          expanded={expandedSections.has('ways')}
                          onToggle={toggleSection}
                          icon={<MessagesSquare size={14} />}
                        />
                        {expandedSections.has('ways') && <WaysConversation itemId={item._id} projectId={item.projectId} />}
                      </>
                    )}

                    {!isCrm && (
                      <>
                      {/* Time Tracking */}
                      <SectionHeader
                        title={`Time Entries (${timeEntries.length})`}
                        section="time"
                        expanded={expandedSections.has('time')}
                        onToggle={toggleSection}
                        icon={<Clock size={14} />}
                      />
                      {expandedSections.has('time') && (
                        <div className="px-4 pb-4">
                          <div className="flex gap-2 mb-3">
                            <input
                              type="number"
                              value={timeForm.hours}
                              onChange={(e) => setTimeForm((p) => ({ ...p, hours: e.target.value }))}
                              placeholder="Hours"
                              className="w-20 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
                              min="0.25"
                              step="0.25"
                            />
                            <input
                              type="text"
                              value={timeForm.description}
                              onChange={(e) => setTimeForm((p) => ({ ...p, description: e.target.value }))}
                              placeholder="What did you work on?"
                              className="flex-1 px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
                            />
                            <button
                              onClick={handleLogTime}
                              disabled={!timeForm.hours}
                              className="px-3 py-1.5 text-xs bg-accent text-white rounded-lg disabled:opacity-30"
                            >
                              Log
                            </button>
                          </div>

                          <div className="space-y-1">
                            {timeEntries.map((e) => (
                              <div key={e._id} className="flex items-center justify-between px-2 py-1 text-xs text-gray-600 dark:text-gray-400">
                                <span>{e.description || '(no description)'}</span>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{e.hours}h</span>
                                  <span className="text-gray-400">{e.date}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                                          </>
                    )}

                    {/* Activity Log */}
                    <SectionHeader
                      title="Activity"
                      section="activity"
                      expanded={expandedSections.has('activity')}
                      onToggle={toggleSection}
                      icon={<Activity size={14} />}
                    />
                    {expandedSections.has('activity') && (
                      <div className="px-4 pb-4">
                        <div className="space-y-1">
                          {activities.map((a) => (
                            <div key={a._id} className="text-xs text-gray-500 dark:text-gray-400 py-1 border-b border-gray-50 dark:border-gray-700">
                              <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">{a.action.replace('_', ' ')}</span>
                              {' · '}
                              <span>{new Date(a.createdAt).toLocaleString()}</span>
                            </div>
                          ))}
                          {activities.length === 0 && (
                            <p className="text-xs text-gray-400">No activity yet</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Transition.Child>
        </div>
      </Transition>

      {showAddChild && item && (
        <CreateWorkItemModal
          projectId={item.projectId}
          parentId={item._id}
          onClose={() => {
            setShowAddChild(false);
            if (item.projectId) fetchByProject(item.projectId);
          }}
        />
      )}
    </>
  );
}

// Collapsible section header
function SectionHeader({
  title,
  section,
  expanded,
  onToggle,
  icon,
}: {
  title: string;
  section: string;
  expanded: boolean;
  onToggle: (s: string) => void;
  icon?: React.ReactNode;
}) {
  return (
    <button
      onClick={() => onToggle(section)}
      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
    >
      {icon}
      <span className="flex-1 text-left">{title}</span>
      {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
    </button>
  );
}
